// Teste de preensão manual (dinamometria) — protocolo bilateral.
//
// Protocolo adotado pelo PersonalMetrics: 3 tentativas em cada mão, alternadamente,
// registrando o melhor resultado de cada lado. A classificação normativa usa o valor da
// mão dominante; os dois valores ficam disponíveis para análise da simetria bilateral.
//
// Este módulo é usado pela tela de coleta (AvaliacaoNeuromuscular) e pelo relatório em PDF
// (Gerador_relatorio), para que ambos usem as mesmas faixas e os mesmos textos.

// ---------------------------------------------------------------------------
// Faixas normativas (kgf) por sexo e idade — 5 níveis.
//
// ATENÇÃO: os cortes de "Regular", "Bom" e "Excelente" são os que já existiam no sistema.
// O antigo nível "Insuficiente" foi dividido em "Fraco" e "Muito fraco" de forma
// sistemática: "Fraco" cobre uma faixa com a mesma largura da faixa "Regular" logo abaixo
// dela, e "Muito fraco" fica abaixo disso. Se houver uma tabela de referência oficial
// com 5 níveis, basta substituir os valores em BASE_* abaixo.
// ---------------------------------------------------------------------------
const BASE_HOMENS = {
  '20-29': { regular: [36, 44], bom: [45, 52] },
  '30-39': { regular: [34, 42], bom: [43, 50] },
  '40-49': { regular: [32, 40], bom: [41, 48] },
  '50-59': { regular: [30, 38], bom: [39, 46] },
  '60-69': { regular: [28, 36], bom: [37, 44] },
  '70+':   { regular: [26, 34], bom: [35, 42] },
};

const BASE_MULHERES = {
  '20-29': { regular: [22, 30], bom: [31, 36] },
  '30-39': { regular: [21, 29], bom: [30, 35] },
  '40-49': { regular: [20, 27], bom: [28, 33] },
  '50-59': { regular: [18, 25], bom: [26, 31] },
  '60-69': { regular: [16, 23], bom: [24, 29] },
  '70+':   { regular: [14, 21], bom: [22, 27] },
};

export const NIVEIS_PREENSAO = ['Muito fraco', 'Fraco', 'Regular', 'Bom', 'Excelente'];

export const CORES_PREENSAO = {
  'Muito fraco': '#d70909',
  'Fraco': '#fb1d1d',
  'Regular': '#ffbb28',
  'Bom': '#66a3ff',
  'Excelente': '#00c457',
};

const VALOR_MAXIMO = 150;

// Faixas contínuas (o máximo de uma faixa encosta no mínimo da seguinte) para que valores
// com casas decimais, como 44,5 kgf, sempre caiam em alguma classificação.
const montarFaixas = ({ regular, bom }) => {
  const larguraRegular = regular[1] - regular[0] + 1;
  const inicioFraco = Math.max(0, regular[0] - larguraRegular);
  return {
    'Muito fraco': { min: 0, max: inicioFraco - 0.01 },
    'Fraco': { min: inicioFraco, max: regular[0] - 0.01 },
    'Regular': { min: regular[0], max: bom[0] - 0.01 },
    'Bom': { min: bom[0], max: bom[1] + 0.99 },
    'Excelente': { min: bom[1] + 1, max: VALOR_MAXIMO },
  };
};

const montarTabela = (base) => Object.fromEntries(
  Object.entries(base).map(([faixaEtaria, cortes]) => [faixaEtaria, montarFaixas(cortes)])
);

export const TABELA_PREENSAO_MANUAL = {
  Masculino: montarTabela(BASE_HOMENS),
  Feminino: montarTabela(BASE_MULHERES),
};

// Faixas no formato usado pelos gráficos de barra: [{ name, range: [min, max], color }].
export const faixasPreensaoManual = (sexo, idade) => {
  const tabela = String(sexo || '').toLowerCase().startsWith('f')
    ? TABELA_PREENSAO_MANUAL.Feminino
    : TABELA_PREENSAO_MANUAL.Masculino;
  const idadeNumero = Number(idade);
  if (!Number.isFinite(idadeNumero)) return [];

  let chave = null;
  for (const faixaEtaria of Object.keys(tabela)) {
    if (faixaEtaria.endsWith('+')) {
      if (idadeNumero >= parseInt(faixaEtaria, 10)) chave = faixaEtaria;
    } else {
      const [min, max] = faixaEtaria.split('-').map(Number);
      if (idadeNumero >= min && idadeNumero <= max) chave = faixaEtaria;
    }
  }
  if (!chave) return [];

  return NIVEIS_PREENSAO.map((nome) => ({
    name: nome,
    range: [tabela[chave][nome].min, tabela[chave][nome].max],
    color: CORES_PREENSAO[nome],
  }));
};

// ---------------------------------------------------------------------------
// Cálculo do resultado bilateral.
// ---------------------------------------------------------------------------
const numero = (x) => {
  const n = Number(String(x ?? '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const melhorTentativa = (tentativas) => {
  const validas = (Array.isArray(tentativas) ? tentativas : []).map(numero).filter((n) => n !== null);
  return validas.length ? Math.max(...validas) : null;
};

// Recebe as 3 tentativas de cada mão e a mão dominante; devolve os valores consolidados.
export const calcularResultadoPreensao = ({ direita, esquerda, maoDominante }) => {
  const melhorDireita = melhorTentativa(direita);
  const melhorEsquerda = melhorTentativa(esquerda);
  const dominante = maoDominante === 'Esquerda' ? 'Esquerda' : 'Direita';
  const valorDominante = dominante === 'Esquerda' ? melhorEsquerda : melhorDireita;

  let assimetriaPercentual = null;
  let maoMaisForte = null;
  if (melhorDireita !== null && melhorEsquerda !== null) {
    const maior = Math.max(melhorDireita, melhorEsquerda);
    const menor = Math.min(melhorDireita, melhorEsquerda);
    assimetriaPercentual = maior > 0 ? Number((((maior - menor) / maior) * 100).toFixed(1)) : 0;
    if (melhorDireita !== melhorEsquerda) maoMaisForte = melhorDireita > melhorEsquerda ? 'Direita' : 'Esquerda';
  }

  return { melhorDireita, melhorEsquerda, maoDominante: dominante, valorDominante, assimetriaPercentual, maoMaisForte };
};

// Diferenças acima de 10% entre as mãos costumam ser tratadas como assimetria relevante.
export const LIMITE_ASSIMETRIA_PERCENTUAL = 10;

export const descreverAssimetria = ({ assimetriaPercentual, maoMaisForte }) => {
  if (assimetriaPercentual === null || assimetriaPercentual === undefined) return null;
  if (assimetriaPercentual === 0) return 'Força de preensão igual entre as mãos.';
  const lado = maoMaisForte === 'Esquerda' ? 'mão esquerda' : 'mão direita';
  if (assimetriaPercentual > LIMITE_ASSIMETRIA_PERCENTUAL) {
    return `Diferença de ${assimetriaPercentual}% entre as mãos, com predomínio da ${lado}. Assimetria acima de ${LIMITE_ASSIMETRIA_PERCENTUAL}%, que merece atenção na prescrição.`;
  }
  return `Diferença de ${assimetriaPercentual}% entre as mãos, com predomínio da ${lado}. Assimetria dentro do esperado.`;
};

// ---------------------------------------------------------------------------
// Textos para o relatório (fonte: "Teste de Preensão Manual.pdf").
// ---------------------------------------------------------------------------
export const OBSERVACAO_PREENSAO_MANUAL =
  'A força de preensão manual é um importante indicador da capacidade de produção de força dos membros superiores e um marcador relacionado à função e à força muscular geral. Seu resultado deve ser interpretado considerando características individuais, como sexo, idade, lateralidade e protocolo utilizado, não devendo ser considerado isoladamente como uma medida direta da força muscular global.';

export const PROTOCOLO_PREENSAO_MANUAL =
  'Protocolo bilateral: 3 tentativas em cada mão, alternadamente, registrando o melhor resultado de cada lado. A classificação normativa considera o valor da mão dominante; os dois valores são usados para a análise da simetria bilateral.';

export const TEXTOS_CLASSIFICACAO_PREENSAO = {
  'Muito fraco': {
    introducao: 'A força de preensão manual é um indicador relacionado à capacidade neuromuscular e funcional do indivíduo. Sua interpretação deve considerar características como sexo, idade, lateralidade e protocolo de avaliação utilizado.',
    resultado: 'O resultado indica força de preensão manual muito abaixo dos valores de referência para o perfil avaliado, demonstrando a necessidade de maior atenção ao desenvolvimento da força muscular.',
    recomendacoes: 'Priorizar o desenvolvimento da força global, associado a exercícios que estimulem mãos, antebraços e membros superiores. Exercícios de puxada, carregamentos e outras tarefas que exijam preensão podem ser incorporados ao programa. Recomenda-se nova avaliação após um período de treinamento estruturado.',
  },
  'Fraco': {
    introducao: 'A força de preensão manual representa um importante indicador da capacidade de produção de força e apresenta relação com aspectos funcionais e neuromusculares. O resultado deve sempre ser analisado de acordo com as características individuais e o protocolo utilizado.',
    resultado: 'O resultado demonstra força de preensão manual abaixo dos valores de referência para o perfil avaliado, indicando possibilidade importante de melhora dessa capacidade.',
    recomendacoes: 'Incluir exercícios de fortalecimento geral e estímulos específicos para mãos, antebraços e membros superiores. Exercícios de puxada, carregamentos e exercícios que exijam manutenção da preensão podem ser utilizados, respeitando a progressão individual das cargas.',
  },
  'Regular': {
    introducao: 'A força de preensão manual é um indicador relacionado à força e à função muscular, devendo ser interpretada em conjunto com outras informações da avaliação física e considerando sexo, idade, lateralidade e protocolo utilizado.',
    resultado: 'O resultado demonstra nível intermediário de força de preensão manual em relação aos valores de referência. Embora o desempenho esteja dentro de uma faixa considerada regular, existe potencial para melhora da capacidade de produção de força.',
    recomendacoes: 'Manter treinamento de força regular e promover progressão gradual das cargas, incluindo exercícios que exijam preensão, puxadas, carregamentos e estabilização. O objetivo deve ser proporcionar evolução progressiva dessa capacidade.',
  },
  'Bom': {
    introducao: 'A força de preensão manual fornece uma informação relevante sobre a capacidade neuromuscular e funcional, mas deve ser analisada em conjunto com os demais componentes da avaliação física e de acordo com as características individuais do avaliado.',
    resultado: 'O resultado indica bom nível de força de preensão manual em relação aos valores de referência, sugerindo adequada capacidade de produção de força para o perfil avaliado.',
    recomendacoes: 'Manter o treinamento de força e a progressão adequada das cargas, buscando preservar e, quando pertinente, aprimorar os níveis atuais. Exercícios multiarticulares, puxadas e carregamentos podem contribuir para a manutenção e evolução dessa capacidade.',
  },
  'Excelente': {
    introducao: 'A força de preensão manual constitui um indicador relacionado ao desempenho neuromuscular e funcional. Mesmo diante de resultados elevados, sua interpretação deve considerar o perfil individual e ser integrada aos demais resultados da avaliação física.',
    resultado: 'O resultado demonstra excelente nível de força de preensão manual em relação aos valores de referência, indicando elevada capacidade de produção de força para o perfil avaliado.',
    recomendacoes: 'Manter os estímulos de força dentro de uma programação estruturada, buscando preservar o desempenho e desenvolver de maneira equilibrada as demais capacidades físicas. A reavaliação periódica permitirá verificar a manutenção ou evolução dos níveis alcançados.',
  },
};

// Avaliações antigas foram salvas com 4 níveis; "Insuficiente" passa a usar o texto de "Fraco".
const ALIAS_CLASSIFICACAO = { Insuficiente: 'Fraco' };

export const textoClassificacaoPreensao = (nome) => {
  if (!nome) return null;
  const chave = ALIAS_CLASSIFICACAO[nome] || nome;
  return TEXTOS_CLASSIFICACAO_PREENSAO[chave] || null;
};
